/**
 * PostHog initialization (product analytics, session replay, client-side
 * error tracking for the authenticated app).
 *
 * Division of labor between the two analytics vendors:
 *   - Google Analytics (see config.ts / AnalyticsProvider) measures the
 *     anonymous public marketing site and loads only there.
 *   - PostHog loads app-wide and is the product-insight system: pageviews
 *     and autocaptured interactions everywhere, identified users and
 *     session replay on authenticated surfaces, and exception autocapture
 *     for client-side errors.
 *
 * Ingestion is first-party: events go to /ingest on our own origin, which
 * next.config.ts rewrites to PostHog's US cloud. That keeps the CSP tight
 * (connect-src stays 'self') and survives the ad-blockers common on
 * corporate networks. If the account ever moves to the EU cloud, change
 * the rewrite destinations in next.config.ts — not this file.
 *
 * Privacy posture:
 *   - Session recording is OFF by default and runs only for signed-in
 *     users ON the authenticated surfaces (/portal, /admin, /onboarding —
 *     see PostHogIdentity). Anonymous visitors are never recorded, and
 *     neither are the public marketing pages.
 *   - Recordings mask ALL input values; rendered page text is captured
 *     unless marked. Add `ph-no-capture` to block an element entirely
 *     (document previews) or `ph-mask` to mask rendered text (tax ids).
 *     Business data visible on admin/portal screens does appear in
 *     recordings — that is what makes replay diagnosable, and PostHog
 *     processes it on our behalf as a service provider.
 *   - Person profiles are created for identified users only, keyed by the
 *     opaque auth id — never email or name.
 *   - Unlike Google Analytics (public pages only), PostHog does receive
 *     app URLs containing record identifiers; that is deliberate —
 *     per-account product analytics requires them.
 */

import posthog from 'posthog-js';
import { detectEnv, isTrackableHost } from './config';

export const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY;

export const IS_POSTHOG_ENABLED = Boolean(POSTHOG_KEY);

let initialized = false;

/**
 * Initialize PostHog once per page load. Safe to call repeatedly; no-ops
 * when the project key is absent or the hostname is not trackable.
 */
export function initPostHog(): void {
  if (initialized) return;
  if (typeof window === 'undefined') return;
  if (!POSTHOG_KEY) return;
  if (!isTrackableHost(window.location.hostname)) return;

  posthog.init(POSTHOG_KEY, {
    api_host: '/ingest',
    ui_host: 'https://us.posthog.com',
    // Pin the library's dated behavior bundle (history-API pageviews for
    // the app router, identified-only person profiles, etc.) so a package
    // upgrade can't silently change capture semantics.
    defaults: '2025-05-24',
    capture_pageview: 'history_change',
    person_profiles: 'identified_only',
    autocapture: true,
    // Uncaught errors and unhandled rejections become $exception events.
    capture_exceptions: true,
    // Recording starts only for signed-in users — see PostHogIdentity.
    disable_session_recording: true,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '.ph-mask',
    },
  });

  // One PostHog project serves every environment (mirroring the single
  // GA4 property): register the environment as a SUPER property so every
  // event — autocapture, pageviews, exceptions, replay metadata — carries
  // it, not just the typed events from events.ts. Derived from the
  // hostname (dev./uat. subdomains vs the apex domain), the same
  // authoritative boundary the isTrackableHost gate uses. Insights and
  // dashboards should filter on `environment`.
  posthog.register({ environment: detectEnv() });

  initialized = true;
}

/** True once init has actually run (key present + trackable host). */
export function isPostHogActive(): boolean {
  return initialized;
}

export { posthog };
